import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { 
  LayoutDashboard, 
  Receipt, 
  BookOpen, 
  FileBarChart, 
  Settings, 
  LogOut,
  Wallet,
  ChevronDown,
  Users,
  Webhook,
  History,
  PieChart,
  Database,
  Sparkles,
  Calendar,
  FileText,
  Landmark
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster } from '../components/ui/sonner';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { selectedFY, setSelectedFY, fyOptions } = useFinancialYear();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/expenses', label: 'Expenses', icon: Receipt },
    { path: '/ledger', label: 'Ledger', icon: BookOpen },
    { path: '/invoices', label: 'Revenue & Invoices', icon: FileText },
    { path: '/estimates', label: 'Estimates', icon: FileText },
    { path: '/funder', label: 'Funder Loans', icon: Landmark },
    { path: '/funder/payout', label: 'Funder Payout', icon: Landmark },
    { path: '/reports', label: 'Reports', icon: FileBarChart },
    { path: '/pnl', label: 'P&L Reports', icon: PieChart },
    { path: '/pnl-report', label: 'New P&L Report (2026+)', icon: Sparkles },
    { path: '/crm-projects', label: 'CRM Projects', icon: Database },
    { path: '/historical-data', label: 'Historical Data', icon: Database },
  ];

  const isAdmin = user?.role === 'ADMIN';

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col fixed h-full z-40">
        {/* Logo */}
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-indigo-950 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold font-heading text-indigo-950">CostLedger</span>
              <p className="text-xs text-slate-500">Expense Tracker</p>
            </div>
          </div>
        </div>

        {/* Global Financial Year Selector */}
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-indigo-600" />
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="h-8 text-sm bg-white border-slate-200" data-testid="global-fy-selector">
                <SelectValue placeholder="Select FY" />
              </SelectTrigger>
              <SelectContent>
                {fyOptions.map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-3">
            Main Menu
          </p>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="pt-4">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-3">
                  Administration
                </p>
              </div>
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid="nav-admin-users"
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Users</span>
              </NavLink>
              <NavLink
                to="/admin/webhooks"
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid="nav-admin-webhooks"
              >
                <Webhook className="w-5 h-5" />
                <span className="font-medium">Webhook Logs</span>
              </NavLink>
              <NavLink
                to="/admin/audit"
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid="nav-admin-audit"
              >
                <History className="w-5 h-5" />
                <span className="font-medium">Audit Logs</span>
              </NavLink>
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-3 px-3 hover:bg-slate-100">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-semibold">
                    {user?.full_name ? getInitials(user.full_name) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-500">{user?.role === 'KAM' ? 'Key Account Manager' : user?.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        <div className="p-8">
          {children}
        </div>
      </main>

      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default Layout;
